<template>
  <transition name="modal">
    <div
      v-if="open"
      class="fixed inset-0 z-[100] flex items-center justify-center p-4 font-NotoSerifKhmer"
    >
      <!-- Backdrop -->
      <div
        class="absolute inset-0 bg-black/60 backdrop-blur-md"
        @click="handleClose('close')"
      ></div>

      <!-- Modal Content -->
      <div
        class="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white/10 backdrop-blur-2xl border border-white/20 rounded-3xl shadow-[0_8px_32px_rgba(0,0,0,0.3)] p-6 md:p-8 animate-scale-up custom-scrollbar"
      >
        <!-- Header -->
        <div class="flex items-center justify-between mb-8">
          <div class="flex items-center gap-4">
            <div
              class="flex items-center justify-center w-12 h-12 rounded-full bg-white/10 border border-white/10 shadow-inner backdrop-blur-md text-emerald-400"
            >
              <i class="fa-solid fa-layer-group text-xl"></i>
            </div>
            <div class="flex flex-col">
              <h2 class="text-2xl font-bold text-white tracking-tight">
                {{ isEditDoc ? t("Edit Guest") : t("Create New Guest") }}
              </h2>
              <span class="text-sm text-white/50 font-medium"
                >{{ t("Manage your guest details") }}</span
              >
            </div>
          </div>

          <button
            @click="handleClose('close')"
            class="text-white/40 hover:text-white transition-colors bg-white/5 hover:bg-white/10 p-2.5 rounded-full"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Form -->
        <form @submit.prevent="handleSubmit" class="space-y-6">
          <!-- Layout Grid -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-6">
            <!-- Name Field -->
            <div class="space-y-2">
              <label for="name" class="text-sm font-medium text-white/80 ml-1">
                {{ t("Guest Name") }} <span class="text-red-400">*</span>
              </label>
              <div class="relative group">
                <input
                  id="name"
                  v-model="name"
                  type="text"
                  :placeholder="t('please enter guest name')"
                  class="glass-input w-full pl-5 pr-4 py-3.5 rounded-2xl outline-none transition-all duration-300"
                  :class="
                    isExistGuestName ? 'border-red-500/50 bg-red-500/10' : ''
                  "
                  required
                />
                <!-- Focus Glow Effect -->
                <div
                  class="absolute inset-0 rounded-2xl ring-1 ring-emerald-500/0 peer-focus:ring-emerald-500/50 transition-all duration-500 pointer-events-none"
                ></div>
              </div>
              <p
                v-if="isExistGuestName"
                class="text-red-400 text-xs font-medium ml-2 flex items-center gap-1 mt-1"
              >
                <i class="fa-solid fa-circle-exclamation"></i> {{ t("guest name already exists") }}
              </p>
            </div>

            <!-- Bride Name -->
            <div class="space-y-2">
              <label for="bride" class="text-sm font-medium text-white/80 ml-1">
                {{ t("Amount") }}<span class="text-red-400">
                  *
                  {{
                    amount > 0 ? numeralFormat(amount, "0,0[.]00")  : ""
                  }} {{ selectedCurrency != null ? selectedCurrency : ""}}</span
                >
              </label>

              <div class="relative group">
                <input
                  id="amount"
                  v-model="amount"
                  type="number"
                  :placeholder="t('please enter amount')"
                  step="0.01"
                  class="glass-input w-full pl-5 pr-4 py-3.5 rounded-2xl outline-none transition-all duration-300"
                  required
                />
                <div
                  class="absolute inset-0 rounded-2xl ring-1 ring-emerald-500/0 peer-focus:ring-emerald-500/50 transition-all duration-500 pointer-events-none"
                ></div>
              </div>
            </div>
            <div class="relative group  ">
              <label for="bride" class="text-sm font-medium text-white/80 ml-1">
                {{ t("Currency") }}<span class="text-red-400"> *</span>
              </label>
              <select
                v-model="selectedCurrency"
                class="glass-input w-full pl-12 pr-10 py-3.5 rounded-2xl outline-none transition-all duration-300 bg-white/5 backdrop-blur-xl border border-white/20 text-white/80 focus:bg-white/10 appearance-none cursor-pointer"
                required
              >
                <option class="bg-slate-800 text-white" disabled value="">
                  {{ t("Please select currency") }}
                </option>
                <option class="bg-slate-800 text-white" value="USD">USD</option>
                <option class="bg-slate-800 text-white" value="KHR">KHR</option>
              </select>

        

              <!-- dropdown arrow -->
              <svg
                class="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60 pointer-events-none"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>

            <div class="relative group  ">
              <label for="bride" class="text-sm font-medium text-white/80 ml-1">
                {{ t("Guest Type") }}
              </label>
              <select
                v-model="guestType"
                class="glass-input w-full pl-12 pr-10 py-3.5 rounded-2xl outline-none transition-all duration-300 bg-white/5 backdrop-blur-xl border border-white/20 text-white/80 focus:bg-white/10 appearance-none cursor-pointer"
                required
              >
                <option class="bg-slate-800 text-white" disabled value="">
                  {{ t("Please select guest type") }}
                </option>
                <option class="bg-slate-800 text-white" value="groom">{{ t("Groom") }}</option>
                <option class="bg-slate-800 text-white" value="bride">{{ t("Bride") }}</option>
                <option class="bg-slate-800 text-white" value="both">{{ t("Both") }}</option>
                <option class="bg-slate-800 text-white" value="none">{{ t("None") }}</option>
              </select>

        

              <!-- dropdown arrow -->
              <svg
                class="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60 pointer-events-none"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>

            <!-- Description -->
            <div class="col-span-1 lg:col-span-2 space-y-2">
              <label
                for="description"
                class="text-sm font-medium text-white/80 ml-1"
              >
                {{ t("Description") }}
              </label>
              <textarea
                id="description"
                v-model="description"
                rows="4"
                :placeholder="t('Enter description...')"
                class="glass-input w-full px-5 py-4 rounded-2xl outline-none resize-none transition-all duration-300"
              ></textarea>
            </div>
          </div>

          <!-- Actions -->
          <div
            class="flex items-center justify-end gap-4 pt-6 border-t border-white/10 mt-2"
          >
            <button
              type="button"
              @click="handleClose('close')"
              :disabled="loading"
              class="px-6 py-2.5 rounded-xl text-white/60 font-medium hover:bg-white/10 hover:text-white transition-colors"
            >
              {{ t("Cancel") }}
            </button>

            <button
              type="submit"
              class="relative overflow-hidden px-8 py-2.5 rounded-xl font-bold text-white shadow-lg shadow-emerald-500/20 transform active:scale-95 transition-all duration-300 group"
              :class="loading ? 'cursor-not-allowed opacity-80' : ''"
            >
              <div
                class="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white transition-all duration-300 group-hover:scale-105"
              ></div>
              <div class="relative flex items-center gap-2">
                <span
                  v-if="loading"
                  class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"
                ></span>
                <span>{{ isEditDoc ? t("Save Changes") : t("Create Guest") }}</span>
              </div>
            </button>
          </div>
        </form>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { serverTimestamp, where } from "firebase/firestore";
import { onMounted, ref, watch, computed } from "vue";
import { projectAuth } from "@/firebase/config";
import useCollection from "@/composables/useDocument";
import { getCollectionQuery } from "@/composables/getCollection";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  visible: Boolean,
  isEditDoc: Boolean,
  doc: Object,
});
const emit = defineEmits(["onClose", "added"]);

const name = ref("");
const originalName = ref(null);
const description = ref("");
const amount = ref(null);
const selectedCurrency = ref('');
const route = useRoute();
const guestType=ref(null);
const guestTypeData=ref([
  {
    id:1,
    name:"groom"
  },
  {
    id:2,
    name:"bride"
  },
  {
    id:3,
    name:"both"
  },
  {
    id:4,
    name:"none"
  }
])



const isExistGuestName = ref(null);
const loading = ref(false);
const open = ref(false);

// Helper for looping inputs

// Setup Logic
onMounted(async () => {
  if (props.visible) open.value = props.visible;
  console.log("route", route.params.id);
  initForm();
});

watch(
  () => props.visible,
  (val) => {
    open.value = val;
  }
);

watch(
  () => props.isEditDoc,
  () => initForm()
);
watch(
  () => props.doc,
  () => initForm()
);

function initForm() {
  if (props.isEditDoc && props.doc) {
    name.value = props.doc.guestName || "";
    guestType.value = props.doc.type || null;

    originalName.value = props.doc.guestName || "";
    amount.value = props.doc.amount || null;
    selectedCurrency.value = props.doc.currency || null;
    description.value = props.doc.description || "";
  } else {
    handleClear();
  }
}

watch(name, async (newV) => {
  if (originalName.value && name.value === originalName.value) return;
  if (!newV) isExistGuestName.value = null;
  if (newV) {
    originalName.value = newV;
    await getCollectionQuery(
      "guests",
      [where("guestName", "==", name.value)],
      (data) => {
        if (data.length > 0) {
          isExistGuestName.value = true;
        } else {
          isExistGuestName.value = false;
        }
      },
      null,
      false
    );
  }
});

const handleClear = () => {
  name.value = "";
  originalName.value = null;
  isExistGuestName.value = null;
  description.value = "";
  amount.value = null;
  selectedCurrency.value = null;
};

watch(() => selectedCurrency.value, (newV) => {
  if (newV) {
    console.log("currency", newV)
  }
})

const handleClose = (status) => {
  open.value = false;
  handleClear();
  setTimeout(() => emit("onClose", status), 200);
};

const { addDocs, updateDocs } = useCollection("guests");

const handleSubmit = async () => {
  try {
    loading.value = true;
    if (isExistGuestName.value) {
      loading.value = false;
      return;
    }
    const requestBody = {
      guestName: name.value,
      userId: projectAuth.currentUser?.uid || "",
      eventId: route.params.id || "",
      type:guestType.value,
      amount: amount.value || null,
      currency: selectedCurrency.value || null,
      description: description.value || "",
    };

    if (!props.isEditDoc) {
      requestBody.createdAt = serverTimestamp();
      requestBody.createdBy = projectAuth.currentUser?.uid || "";
      await addDocs(requestBody);
      handleClose("create");
    } else {
      requestBody.updatedAt = serverTimestamp();
      requestBody.updatedBy = projectAuth.currentUser?.uid || "";

      await updateDocs(props.doc.id, requestBody);
      handleClose("update");
    }
  } catch (err) {
    loading.value = false;
    console.error("Error submitting form:", err);
  }
};
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.animate-scale-up {
  animation: scaleUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes scaleUp {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

:deep(.p-datepicker) {
  @apply rounded-2xl border border-white/10 bg-slate-900/90 backdrop-blur-xl shadow-xl;
}

:deep(.p-datepicker-input) {
  @apply w-full bg-white/5 backdrop-blur-xl border border-white/20 text-white placeholder-indigo-200/50 rounded-2xl py-4 pl-12 pr-4 transition-all font-medium;
}

:deep(.p-datepicker-input:focus) {
  @apply outline-none ring-2 ring-indigo-500/50 bg-white/10;
}

/* Calendar Panel Styles */
:deep(.p-datepicker-header) {
  @apply border-b border-white/10 bg-transparent text-white pt-4 pb-2;
}

:deep(.p-datepicker-title) {
  @apply text-white font-bold;
}

:deep(.p-datepicker-prev-icon),
:deep(.p-datepicker-next-icon) {
  @apply text-white/70 hover:text-white transition-colors;
}

:deep(.p-datepicker-table) {
  @apply my-2;
}

:deep(.p-datepicker-table th) {
  @apply text-indigo-300 font-medium p-2;
}

:deep(.p-datepicker-table td) {
  @apply p-1;
}

:deep(.p-datepicker-table td > span) {
  @apply text-white/90 w-8 h-8 rounded-full transition-all border border-transparent;
}

:deep(.p-datepicker-table td > span:hover) {
  @apply bg-white/10 text-white;
}

:deep(.p-datepicker-table td.p-datepicker-today > span) {
  @apply bg-indigo-500/20 text-indigo-300 border-indigo-500/50 font-bold;
}

:deep(.p-datepicker-table td > span.p-highlight) {
  @apply bg-indigo-600 text-white shadow-lg shadow-indigo-600/20;
}

:deep(.p-button-text) {
  @apply text-indigo-300 hover:bg-indigo-500/10 hover:text-indigo-200;
}
/* PrimeVue InputNumber glass style */
.p-inputnumber .p-inputtext {
  background: rgba(255, 255, 255, 0.08) !important;
  backdrop-filter: blur(12px);
  border-radius: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: white;
}

.p-inputnumber .p-inputtext::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.p-inputnumber .p-inputtext:focus {
  outline: none;
  box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.5); /* emerald */
}
</style>